import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button, Table, Form, ButtonGroup,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { ApiProxyGuard } from '../components/ApiProxyGuard';
import { ApiHealthStrip } from '../components/ApiHealthStrip';
import { ebillFetch, ebillDownloadUrl, isEbillProxyEnabled } from '../ebill/ebillClient';

/**
 * ebill — E-Rechnung API (ZUGFeRD/Factur-X PDF/A-3 + XRechnung-CII, Validierung
 * via KoSIT, Legacy-PDF-Konvertierung via Ollama). Verdrahtet gegen /proxy/ebill.
 */
// Duennes Delegat auf die gemeinsame Guard-Komponente (#116) — die <ProxyGuard>-
// Verwendungen unten bleiben unveraendert.
function ProxyGuard({ children }) {
  return (
    <ApiProxyGuard enabled={isEbillProxyEnabled()} label="ebill" envVar="EBILL_API_URL" proxyPath="/proxy/ebill">
      {children}
    </ApiProxyGuard>
  );
}

const SAMPLE = JSON.stringify({
  invoice_details: {
    number: 'RE-2026-0001', date: '2026-07-10', currency: 'EUR', leitweg_id: '04011000-1234512345-06',
    due_date: '2026-08-09', iban: 'DE02120300000000202051',
  },
  seller: {
    name: 'Valitype GmbH', vat_id: 'DE123456789', street: 'Musterstr. 1', postcode: '52062', city: 'Aachen', country_id: 'DE',
    contact: { name: 'Max Muster', email: 'billing@valitype.de', phone: '+49 241 123456' },
  },
  buyer: { name: 'Stadt Aachen', street: 'Rathaus', postcode: '52062', city: 'Aachen', country_id: 'DE' },
  items: [{ description: 'Beratung', quantity: 2, unit_price: 100, tax_rate: 19 }],
}, null, 2);

const DOC_TYPES = [
  { code: '380', label: 'Rechnung (380)' },
  { code: '381', label: 'Gutschrift (381)' },
  { code: '384', label: 'Korrektur / Storno (384)' },
];

const CARDS = [
  { to: '/admin/apis/ebill/generate', icon: '🧾', title: 'Rechnung erzeugen', status: 'live',
    description: 'JSON → ZUGFeRD/Factur-X (PDF/A-3) oder XRechnung (CII-XML). Rechnung / Gutschrift / Storno.' },
  { to: '/admin/apis/ebill/validate', icon: '✅', title: 'Validieren', status: 'live',
    description: 'Business-Rules + XML-Wohlgeformtheit + KoSIT-Schematron.' },
  { to: '/admin/apis/ebill/artifacts', icon: '📁', title: 'Artefakte', status: 'live',
    description: 'Zuletzt erzeugte Dokumente ansehen & herunterladen.' },
  { to: '/admin/apis/ebill/stats', icon: '📊', title: 'Statistik', status: 'live',
    description: 'Dokumente nach Art/Herkunft, 14-Tage-Verlauf, Mandanten-Kontingente.' },
  { to: '/admin/apis/ebill/settings', icon: '⚙️', title: 'Einstellungen', status: 'live',
    description: 'Storage/KoSIT/OCR/Ollama-Status, Credit-Defaults, Kontingent-Reset.' },
];

// ── Übersicht ───────────────────────────────────────────────────────────
export function EbillOverviewPage() {
  const health = useQuery({ queryKey: ['ebill-health'], queryFn: () => ebillFetch('/healthz'), refetchInterval: 30_000, retry: false, enabled: isEbillProxyEnabled() });
  const mon = useQuery({ queryKey: ['ebill-monitoring'], queryFn: () => ebillFetch('/v1/monitoring'), refetchInterval: 20_000, retry: false, enabled: isEbillProxyEnabled() });
  const m = mon.data || {};

  return (
    <div>
      <PageHeader title="E-Rechnung API · Übersicht"
        subtitle="ZUGFeRD/XRechnung erzeugen, validieren (KoSIT) und konvertieren (Ollama)." />
      <ProxyGuard>
        <ApiHealthStrip health={health}>
          {m.status && <Badge color={m.status === 'ok' ? 'success' : 'warning'} pill className="ms-2">{m.status}</Badge>}
        </ApiHealthStrip>
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Artefakte</CardTitle>
            <CardText className="fs-4">{m.artifacts_total ?? '–'}</CardText>
            <div className="small text-muted">{m.artifacts_generate ?? 0} generate · {m.artifacts_wrap ?? 0} wrap · {m.artifacts_convert ?? 0} convert</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">KoSIT-Validator
            <Badge color={m.kosit_configured ? 'success' : 'secondary'} pill className="ms-2">{m.kosit_configured ? 'aktiv' : 'aus'}</Badge></CardTitle>
            <div className="small text-muted">Schematron (EN16931/XRechnung)</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Konvertierung
            <Badge color={m.ollama_configured && m.convert_enabled ? 'success' : 'secondary'} pill className="ms-2">{m.ollama_configured && m.convert_enabled ? 'aktiv' : 'aus'}</Badge></CardTitle>
            <div className="small text-muted">Legacy-PDF → E-Invoice (Ollama)</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Storage / Credits</CardTitle>
            <div className="small"><Badge color="light" className="text-dark border">{m.storage_backend || '–'}</Badge>
              {m.storage_ready ? <Badge color="success" pill className="ms-1">ready</Badge> : <Badge color="warning" pill className="ms-1">n/a</Badge>}</div>
            <div className="small text-muted mt-1">default-Kontingent: {m.credits_default_remaining ?? '–'} Seiten</div>
          </CardBody></Card></Col>
        </Row>
        <OverviewGrid cards={CARDS} />
      </ProxyGuard>
    </div>
  );
}

// ── Erzeugen ────────────────────────────────────────────────────────────
export function EbillGeneratePage() {
  const [json, setJson] = useState(SAMPLE);
  const [docType, setDocType] = useState('380');
  const [result, setResult] = useState(null);
  const [xml, setXml] = useState(null);

  const gen = useMutation({
    mutationFn: async ({ kind }) => {
      let invoice;
      try { invoice = JSON.parse(json); } catch { throw new Error('Rechnung ist kein gültiges JSON'); }
      // Dokumentart injizieren (380 Rechnung / 381 Gutschrift / 384 Storno).
      invoice = { ...invoice, invoice_details: { ...invoice.invoice_details, type_code: docType } };
      if (kind === 'xrechnung') {
        const text = await ebillFetch('/v1/generate/xrechnung', { method: 'POST', body: invoice, responseType: 'text' });
        return { kind, xml: text };
      }
      const res = await ebillFetch('/v1/generate', { method: 'POST', body: invoice });
      return { kind, res };
    },
    onSuccess: (d) => { if (d.kind === 'xrechnung') { setXml(d.xml); setResult(null); } else { setResult(d.res); setXml(null); } },
  });

  return (
    <div>
      <PageHeader title="E-Rechnung · Erzeugen" subtitle="JSON → ZUGFeRD/Factur-X (PDF/A-3) oder XRechnung (CII-XML)." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Row className="g-2 align-items-end mb-2">
              <Col md={4}>
                <Label className="small">Dokumentart (BT-3)</Label>
                <Input type="select" bsSize="sm" value={docType} onChange={(e) => setDocType(e.target.value)}>
                  {DOC_TYPES.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
                </Input>
              </Col>
            </Row>
            <Label className="small">Rechnung (JSON)</Label>
            <Input type="textarea" bsSize="sm" rows={14} style={{ fontFamily: 'monospace' }} value={json} onChange={(e) => setJson(e.target.value)} />
            <ButtonGroup className="mt-2">
              <Button size="sm" color="primary" disabled={gen.isPending} onClick={() => gen.mutate({ kind: 'zugferd' })}>ZUGFeRD (PDF/A-3)</Button>
              <Button size="sm" color="secondary" disabled={gen.isPending} onClick={() => gen.mutate({ kind: 'xrechnung' })}>XRechnung (XML)</Button>
            </ButtonGroup>
            {gen.isPending && <Spinner size="sm" className="ms-2" />}
          </Form>
          {gen.isError && <Alert color="danger" className="small mt-2">{gen.error?.message}</Alert>}
        </CardBody></Card>

        {result && (
          <Card className="mb-3"><CardBody>
            <CardTitle tag="h6">Erzeugt <Badge color="success" pill>{result.format}</Badge></CardTitle>
            <div className="small">
              <div>invoice_id: <code>{result.invoice_id}</code></div>
              <div>eingebettet: <code>{result.embedded_xml_filename}</code> · storage: <code>{result.storage}</code></div>
              <a className="btn btn-sm btn-outline-primary mt-2" href={ebillDownloadUrl(result.invoice_id)} target="_blank" rel="noreferrer">PDF herunterladen ↓</a>
            </div>
          </CardBody></Card>
        )}
        {xml && (
          <Card><CardBody>
            <CardTitle tag="h6">XRechnung (CII-XML)</CardTitle>
            <Input type="textarea" bsSize="sm" rows={12} readOnly style={{ fontFamily: 'monospace' }} value={xml} />
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}

// ── Validieren ──────────────────────────────────────────────────────────
export function EbillValidatePage() {
  const [mode, setMode] = useState('invoice'); // 'invoice' | 'xml'
  const [text, setText] = useState(SAMPLE);

  const val = useMutation({
    mutationFn: async () => {
      if (mode === 'xml') return ebillFetch('/v1/validate', { method: 'POST', body: { xml: text } });
      let invoice;
      try { invoice = JSON.parse(text); } catch { throw new Error('Kein gültiges JSON'); }
      return ebillFetch('/v1/validate', { method: 'POST', body: { invoice } });
    },
  });
  const r = val.data;

  return (
    <div>
      <PageHeader title="E-Rechnung · Validieren" subtitle="Business-Rules + XML-Wohlgeformtheit + KoSIT-Schematron." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <ButtonGroup size="sm" className="mb-2">
            <Button color={mode === 'invoice' ? 'primary' : 'light'} onClick={() => setMode('invoice')}>JSON-Rechnung</Button>
            <Button color={mode === 'xml' ? 'primary' : 'light'} onClick={() => setMode('xml')}>CII-XML</Button>
          </ButtonGroup>
          <Input type="textarea" bsSize="sm" rows={12} style={{ fontFamily: 'monospace' }} value={text} onChange={(e) => setText(e.target.value)} />
          <Button size="sm" color="primary" className="mt-2" disabled={val.isPending} onClick={() => val.mutate()}>{val.isPending ? <><Spinner size="sm" /> prüfe …</> : 'Validieren'}</Button>
          {val.isError && <Alert color="danger" className="small mt-2">{val.error?.message}</Alert>}
        </CardBody></Card>

        {r && (
          <Card><CardBody>
            <CardTitle tag="h6">Ergebnis <Badge color={r.valid ? 'success' : 'danger'} pill>{r.valid ? 'valid' : 'invalid'}</Badge>
              <span className="text-muted small ms-2">{r.rules_engine}</span>
              {r.kosit?.executed && <Badge color={r.kosit.accepted ? 'success' : 'warning'} pill className="ms-2">KoSIT: {r.kosit.accepted ? 'accept' : 'reject'}</Badge>}
              {r.kosit && !r.kosit.executed && <Badge color="secondary" pill className="ms-2" title={r.kosit.reason || ''}>KoSIT: n/a</Badge>}
            </CardTitle>
            {(!r.issues || !r.issues.length) ? <CardText className="small text-success">Keine Verstöße.</CardText> : (
              <Table size="sm" responsive className="small">
                <thead><tr><th>Code</th><th>Meldung</th></tr></thead>
                <tbody>{r.issues.map((i, k) => (<tr key={k}><td><code>{i.code}</code></td><td>{i.message}</td></tr>))}</tbody>
              </Table>
            )}
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}

// ── Artefakte ───────────────────────────────────────────────────────────
export function EbillArtifactsPage() {
  const qc = useQueryClient();
  const arts = useQuery({ queryKey: ['ebill-artifacts'], queryFn: () => ebillFetch('/v1/invoices?limit=100'), refetchInterval: 20_000, retry: false, enabled: isEbillProxyEnabled() });
  const items = arts.data?.items || [];

  return (
    <div>
      <PageHeader title="E-Rechnung · Artefakte" subtitle="Zuletzt erzeugte Dokumente (PDF/A-3)." />
      <ProxyGuard>
        <Card><CardBody>
          <CardTitle tag="h6">Artefakte <Badge color="secondary" pill>{items.length}</Badge>
            <Button size="sm" color="link" className="p-0 ms-2" onClick={() => qc.invalidateQueries({ queryKey: ['ebill-artifacts'] })}>neu laden</Button></CardTitle>
          {arts.isLoading ? <Spinner size="sm" />
            : !items.length ? <CardText className="small text-muted">Noch keine Artefakte.</CardText>
            : (
              <Table size="sm" responsive hover>
                <thead><tr><th>Rechnung</th><th>Standard</th><th>Herkunft</th><th>Seiten</th><th>Erstellt</th><th>Storage</th><th></th></tr></thead>
                <tbody>{items.map((a) => (
                  <tr key={a.id}>
                    <td>{a.invoice_number || <span className="text-muted">—</span>}<div className="small text-muted">{a.invoice_date || ''}</div></td>
                    <td><Badge color="light" className="text-dark border">{a.standard}</Badge></td>
                    <td className="small">{a.origin}</td>
                    <td>{a.page_count}</td>
                    <td className="small text-muted">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                    <td className="small">{a.storage}</td>
                    <td><a href={ebillDownloadUrl(a.id)} target="_blank" rel="noreferrer">PDF ↓</a></td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Statistik ───────────────────────────────────────────────────────────
export function EbillStatsPage() {
  const stats = useQuery({ queryKey: ['ebill-stats'], queryFn: () => ebillFetch('/v1/stats'), refetchInterval: 30_000, retry: false, enabled: isEbillProxyEnabled() });
  const s = stats.data || {};
  const a = s.artifacts || {};
  const days = a.last_14_days || [];
  const maxN = Math.max(1, ...days.map((d) => d.n));

  return (
    <div>
      <PageHeader title="E-Rechnung · Statistik" subtitle="Dokumente nach Art/Herkunft, 14-Tage-Verlauf, Mandanten-Kontingente." />
      <ProxyGuard>
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Dokumente gesamt</CardTitle><CardText className="fs-4">{a.total ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={5}><Card><CardBody><CardTitle tag="h6">Nach Herkunft</CardTitle>
            <div className="small">{Object.entries(a.by_origin || {}).map(([k, v]) => <Badge key={k} color="light" className="text-dark border me-1">{k}: {v}</Badge>)}
              {!Object.keys(a.by_origin || {}).length && <span className="text-muted">–</span>}</div>
          </CardBody></Card></Col>
          <Col md={4}><Card><CardBody><CardTitle tag="h6">Nach Standard</CardTitle>
            <div className="small">{Object.entries(a.by_standard || {}).map(([k, v]) => <Badge key={k} color="light" className="text-dark border me-1">{k}: {v}</Badge>)}
              {!Object.keys(a.by_standard || {}).length && <span className="text-muted">–</span>}</div>
          </CardBody></Card></Col>
        </Row>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Verlauf (14 Tage)</CardTitle>
          {stats.isLoading ? <Spinner size="sm" />
            : !days.length ? <CardText className="small text-muted">Keine Daten.</CardText>
            : (
              <div className="d-flex align-items-end gap-1" style={{ height: 80 }}>
                {days.map((d) => <div key={d.day} title={`${d.day}: ${d.n}`} className="flex-fill bg-primary rounded-top" style={{ height: `${Math.round((d.n / maxN) * 100)}%`, minHeight: 2 }} />)}
              </div>
            )}
          {days.length > 0 && <div className="small text-muted mt-1">{days[0].day} … {days[days.length - 1].day}</div>}
        </CardBody></Card>
        <Card><CardBody>
          <CardTitle tag="h6">Mandanten-Kontingente <Badge color="secondary" pill>{s.tenants?.total ?? 0}</Badge></CardTitle>
          {(s.tenants?.credits || []).length ? (
            <Table size="sm" responsive hover>
              <thead><tr><th>Mandant</th><th>Testseiten frei</th><th>Monats-Rest</th></tr></thead>
              <tbody>{s.tenants.credits.map((c) => (
                <tr key={c.tenant_id}><td><code>{c.tenant_id}</code></td><td>{c.free_remaining}</td><td>{c.monthly_remaining}</td></tr>
              ))}</tbody>
            </Table>
          ) : <CardText className="small text-muted">Noch keine Mandanten.</CardText>}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Einstellungen ───────────────────────────────────────────────────────
export function EbillSettingsPage() {
  const qc = useQueryClient();
  const stats = useQuery({ queryKey: ['ebill-settings'], queryFn: () => ebillFetch('/v1/stats'), refetchInterval: 30_000, retry: false, enabled: isEbillProxyEnabled() });
  const c = stats.data?.config || {};
  const credits = stats.data?.tenants?.credits || [];
  const reset = useMutation({
    mutationFn: (tenant) => ebillFetch(`/v1/stats/credits/${encodeURIComponent(tenant)}/reset`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ebill-settings'] }),
  });
  const flag = (v) => (v ? <Badge color="success" pill>an</Badge> : <Badge color="secondary" pill>aus</Badge>);

  return (
    <div>
      <PageHeader title="E-Rechnung · Einstellungen" subtitle="Dienst-Status, Convert-Strategie und Mandanten-Kontingente." />
      <ProxyGuard>
        <Row className="g-3 mb-3">
          <Col md={6}><Card><CardBody>
            <CardTitle tag="h6">Dienste & Konfiguration</CardTitle>
            <Table size="sm" borderless className="small mb-0"><tbody>
              <tr><td>Storage-Backend</td><td><code>{c.storage_backend || '–'}</code> {c.storage_ready ? <Badge color="success" pill>ready</Badge> : <Badge color="warning" pill>n/a</Badge>}</td></tr>
              <tr><td>KoSIT-Validator</td><td>{flag(c.kosit_configured)}</td></tr>
              <tr><td>OCR-Dienst</td><td>{flag(c.ocr_configured)}</td></tr>
              <tr><td>Ollama (LLM)</td><td>{flag(c.ollama_configured)}</td></tr>
              <tr><td>Convert</td><td>{flag(c.convert_enabled)} · Strategie <code>{c.convert_strategy || '–'}</code></td></tr>
            </tbody></Table>
          </CardBody></Card></Col>
          <Col md={6}><Card><CardBody>
            <CardTitle tag="h6">Credit-Defaults</CardTitle>
            <div className="small">Durchsetzung: {flag(c.credit_enforce)}</div>
            <div className="small">Einmalige Testseiten: <strong>{c.credit_free_test_pages ?? '–'}</strong></div>
            <div className="small">Monatliches Limit: <strong>{c.credit_monthly_pages ?? '–'}</strong> Seiten</div>
            <div className="small text-muted mt-2">Werte änderbar über die ebill-ConfigMap (<code>CREDIT_*</code>).</div>
          </CardBody></Card></Col>
        </Row>
        <Card><CardBody>
          <CardTitle tag="h6">Mandanten-Kontingente</CardTitle>
          {credits.length ? (
            <Table size="sm" responsive hover>
              <thead><tr><th>Mandant</th><th>Testseiten frei</th><th>Monats-Rest</th><th></th></tr></thead>
              <tbody>{credits.map((t) => (
                <tr key={t.tenant_id}>
                  <td><code>{t.tenant_id}</code></td><td>{t.free_remaining}</td><td>{t.monthly_remaining}</td>
                  <td><Button size="sm" color="link" className="p-0" disabled={reset.isPending} onClick={() => reset.mutate(t.tenant_id)}>zurücksetzen</Button></td>
                </tr>
              ))}</tbody>
            </Table>
          ) : <CardText className="small text-muted">Noch keine Mandanten.</CardText>}
          {reset.isError && <Alert color="danger" className="small mt-2">{reset.error?.message}</Alert>}
          {reset.isSuccess && <Alert color="success" className="small mt-2">Kontingent zurückgesetzt.</Alert>}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}
